import { CUBE_SIZE } from '@qoobd/rules';
import { formatChallengeTime } from '../game/challenge';

/** Everything a brag card shows. */
export interface CardData {
  eyebrow: string;
  title: string;
  lose: boolean;
  /** e.g. "1:14.2 · 31 steps · vs Relentless" */
  stats: string;
  hook: string;
  url: string;
  /** Room coordinates along each path, in order. */
  pathA: readonly (readonly [number, number, number])[];
  pathB: readonly (readonly [number, number, number])[];
  core: readonly [number, number, number] | null;
  labelA: string;
  labelB: string;
}

const W = 1200;
const H = 630;
const C = {
  bg: '#16191E',
  panel: '#23272E',
  steel: '#3A3F47',
  pewter: '#6B727C',
  bone: '#E8E3D9',
  amber: '#FFB000',
  ice: '#A9C4DB',
  red: '#E5484D',
  cyan: '#3FD0D4',
};

/** Isometric projection of a room coordinate into the card's mini-cube area. */
function iso(p: readonly [number, number, number], cx: number, cy: number, s: number): [number, number] {
  const [x, y, z] = p;
  const c30 = Math.cos(Math.PI / 6);
  return [cx + (x - z) * c30 * s, cy + (x + z) * 0.5 * s - y * s];
}

function drawPath(g: CanvasRenderingContext2D, path: CardData['pathA'], color: string, cx: number, cy: number, s: number): void {
  if (!path.length) return;
  g.strokeStyle = color;
  g.lineWidth = 3;
  g.lineJoin = 'round';
  g.lineCap = 'round';
  g.beginPath();
  path.forEach((p, i) => {
    const [x, y] = iso(p, cx, cy, s);
    if (i) g.lineTo(x, y);
    else g.moveTo(x, y);
  });
  g.stroke();
  const [sx, sy] = iso(path[0]!, cx, cy, s);
  g.fillStyle = C.bg;
  g.beginPath();
  g.arc(sx, sy, 6, 0, Math.PI * 2);
  g.fill();
  g.lineWidth = 2.5;
  g.stroke();
  const [ex, ey] = iso(path[path.length - 1]!, cx, cy, s);
  g.fillStyle = color;
  g.beginPath();
  g.arc(ex, ey, 7, 0, Math.PI * 2);
  g.fill();
}

/** Draws the share image: result on the left, both paths through the cube on the right. */
export async function drawCard(d: CardData): Promise<Blob> {
  try {
    await Promise.all([
      document.fonts.load('800 80px "Big Shoulders Display"'),
      document.fonts.load('500 24px "IBM Plex Mono"'),
      document.fonts.load('400 30px "IBM Plex Sans"'),
    ]);
  } catch {
    /* fall back to system fonts */
  }
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const g = canvas.getContext('2d')!;
  g.fillStyle = C.bg;
  g.fillRect(0, 0, W, H);
  const glow = g.createRadialGradient(860, 300, 20, 860, 300, 420);
  glow.addColorStop(0, 'rgba(255,176,0,0.10)');
  glow.addColorStop(1, 'rgba(255,176,0,0)');
  g.fillStyle = glow;
  g.fillRect(0, 0, W, H);

  const display = '"Big Shoulders Display", "Arial Narrow", Impact, sans-serif';
  const mono = '"IBM Plex Mono", ui-monospace, Menlo, monospace';
  const sans = '"IBM Plex Sans", system-ui, sans-serif';

  // Wordmark
  g.font = `800 54px ${display}`;
  g.textBaseline = 'alphabetic';
  let x = 64;
  for (const [ch, col] of [['Q', C.bone], ['O', C.amber], ['O', C.amber], ['B', C.bone], ['D', C.bone]] as const) {
    g.fillStyle = col;
    g.fillText(ch, x, 104);
    x += g.measureText(ch).width + 3;
  }

  g.font = `500 20px ${mono}`;
  g.fillStyle = C.amber;
  g.fillText(d.eyebrow.toUpperCase().split('').join(String.fromCharCode(8202)), 64, 190);
  g.font = `800 92px ${display}`;
  g.fillStyle = d.lose ? C.red : C.amber;
  g.fillText(d.title, 60, 284);
  g.font = `500 26px ${mono}`;
  g.fillStyle = C.bone;
  g.fillText(d.stats, 64, 340);

  // Hook, wrapped
  g.font = `400 34px ${sans}`;
  g.fillStyle = C.bone;
  const words = d.hook.split(' ');
  let line = '';
  let y = 420;
  for (const w of words) {
    const t = line ? `${line} ${w}` : w;
    if (g.measureText(t).width > 560 && line) {
      g.fillText(line, 64, y);
      y += 44;
      line = w;
    } else line = t;
  }
  g.fillText(line, 64, y);

  g.font = `500 20px ${mono}`;
  g.fillStyle = C.pewter;
  g.fillText(d.url.replace(/^https?:\/\//, ''), 64, 572);

  // Mini-cube with both paths
  const s = 27;
  const cx = 860;
  const cy = 300; // the cube spans cy ± (CUBE_SIZE - 1) * s vertically
  const n = CUBE_SIZE - 1;
  const corners: [number, number, number][] = [];
  for (const a of [0, n]) for (const b of [0, n]) for (const c of [0, n]) corners.push([a, b, c]);
  g.strokeStyle = C.steel;
  g.lineWidth = 1.5;
  for (let i = 0; i < 8; i++)
    for (let j = i + 1; j < 8; j++) {
      const p = corners[i]!, q = corners[j]!;
      const diff = (p[0] !== q[0] ? 1 : 0) + (p[1] !== q[1] ? 1 : 0) + (p[2] !== q[2] ? 1 : 0);
      if (diff !== 1) continue;
      const [x1, y1] = iso(p, cx, cy, s);
      const [x2, y2] = iso(q, cx, cy, s);
      g.beginPath();
      g.moveTo(x1, y1);
      g.lineTo(x2, y2);
      g.stroke();
    }
  drawPath(g, d.pathB, C.ice, cx, cy, s);
  drawPath(g, d.pathA, C.amber, cx, cy, s);
  if (d.core) {
    const [kx, ky] = iso(d.core, cx, cy, s);
    g.fillStyle = C.cyan;
    g.beginPath();
    g.moveTo(kx, ky - 10);
    g.lineTo(kx + 8, ky);
    g.lineTo(kx, ky + 10);
    g.lineTo(kx - 8, ky);
    g.closePath();
    g.fill();
  }
  // Legend
  g.font = `500 18px ${mono}`;
  const legend: [string, string][] = [
    [C.amber, d.labelA],
    [C.ice, d.labelB],
    ...(d.core ? ([[C.cyan, 'CORE']] as [string, string][]) : []),
  ];
  let lx = 690;
  for (const [col, label] of legend) {
    g.fillStyle = col;
    g.fillRect(lx, 562, 14, 14);
    g.fillStyle = C.bone;
    g.fillText(label, lx + 22, 576);
    lx += 44 + g.measureText(label).width;
  }

  return await new Promise<Blob>((resolve, reject) => canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('no image'))), 'image/png'));
}

export { formatChallengeTime };

/** Share an image and link where the device supports it; returns how it went. */
export async function shareCard(blob: Blob, text: string, url: string): Promise<'shared' | 'cancelled' | 'unsupported'> {
  const file = new File([blob], 'qoobd.png', { type: 'image/png' });
  const nav = navigator as Navigator & { canShare?: (d: ShareData) => boolean };
  try {
    if (nav.share && nav.canShare?.({ files: [file] })) {
      await nav.share({ files: [file], title: 'QOOBD', text: `${text} ${url}` });
      return 'shared';
    }
    if (nav.share) {
      await nav.share({ title: 'QOOBD', text, url });
      return 'shared';
    }
  } catch (e) {
    if ((e as DOMException).name === 'AbortError') return 'cancelled';
  }
  return 'unsupported';
}

export function saveImage(blob: Blob): void {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = 'qoobd.png';
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 2000);
}

export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}
